import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ADS_CONFIG, isAdsenseConfigured, slotIdForPlacement } from "@/lib/adsConfig";
import { hasAdvertisingConsent } from "@/lib/cookieConsent";
import { isSensitiveRoute } from "@/lib/sensitiveRoutes";

/**
 * Reusable AdSense placement (§17). Every real ad on the site renders through this
 * component — no raw AdSense markup/script lives in any page.
 *
 * Rendering rules, in order:
 *  1. Sensitive route (login/admin/account/billing/checkout/etc.) → always null.
 *     Checked first and unconditionally, regardless of every other flag below — this
 *     is the defense-in-depth backstop §80's tests rely on, independent of whether a
 *     page author also correctly avoided placing <AdSlot> there in the first place.
 *  2. Non-production (dev/test) → nothing rendered. This guarantees zero real
 *     AdSense traffic and prevents local-only labels from appearing in page QA.
 *  3. Production, but not configured (disabled, or this placement has no slot ID) →
 *     nothing rendered. A "not configured" placeholder would itself be a visible,
 *     slightly unprofessional artifact on a real page — better to simply not show
 *     the slot until it's genuinely ready.
 *  4. Production, configured, but the visitor has not given advertising consent yet →
 *     nothing rendered (§33 — never fire ads before consent).
 *  5. Otherwise → the real Google <ins class="adsbygoogle"> unit, pushed to the
 *     adsbygoogle queue exactly once per mount.
 */
export default function AdSlot({ placement, format = "auto", responsive = true, minWidth, className = "" }) {
  const { pathname } = useLocation();
  const insRef = useRef(null);
  const pushedRef = useRef(false);

  const isProduction = process.env.NODE_ENV === "production";
  const blocked = isSensitiveRoute(pathname);
  const slotId = slotIdForPlacement(placement);
  const configured = isAdsenseConfigured() && Boolean(slotId);
  const consented = hasAdvertisingConsent();
  const canRenderRealAd = isProduction && !blocked && configured && consented;

  useEffect(() => {
    if (!canRenderRealAd || pushedRef.current || !insRef.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch {
      // AdSense script not ready yet, blocked by an ad blocker/extension, or any other
      // client-side failure — never let an ad failure break the surrounding page.
    }
  }, [canRenderRealAd]);

  if (blocked) return null;

  if (!isProduction) return null;

  if (!configured || !consented) return null;

  return (
    <ins
      ref={insRef}
      data-testid={`adslot-${placement}`}
      className={`adsbygoogle block ${className}`}
      style={{ display: "block", ...(minWidth ? { minWidth } : {}) }}
      data-ad-client={ADS_CONFIG.clientId}
      data-ad-slot={slotId}
      data-ad-format={format}
      data-full-width-responsive={responsive ? "true" : "false"}
    />
  );
}
